import { set } from 'lodash';

const $inject = [
  '$http',
  '$q',
  '$filter',
  '$rootScope',
  'apiService',
  'cartService',
  'settingsService',
  'travelFiltersService',
  'APP_CONFIG',
  'itinerariesService',
  '$stateParams',
];

const travelService = function travelService(
  $http,
  $q,
  $filter,
  $rootScope,
  apiService,
  cartService,
  settingsService,
  travelFiltersService,
  APP_CONFIG,
  itinerariesService,
  $stateParams
) {
  const service = {
    resultsMaster: [],
    results: [],
    packageResults: [],
    setPackageResults: (results) => (service.packageResults = results),
    getPackageResults: () => service.packageResults,
    fareClasses: [],
    flightMessages: [],
    hasLoaded: false,
    hasResults: false,
    hasPackages: false,
    hasBackendError: false,
    policyMessagePopups: [],
    policyViolations: [],
    showPackages: localStorage.getItem('showPackages') === 'true',
    init: init,
    getResults: getResults,
    setResults: setResults,
    getSeatMap: getSeatMap,
    getSeatmapSearches: getSeatmapSearches,
    getTravelDetail: getTravelDetail,
    getFareRules: getFareRules,
    prepData: prepData,
    addFilter: addFilter,
    addFilters: addFilters,
    updateFilters: updateFilters,
    removeFilter: removeFilter,
    process: process,
    sort: sort,
    itemAddedToCart: itemAddedToCart,
    getFareIsOutOfPolicyMsg: getFareIsOutOfPolicyMsg,
    processAllLinked: processAllLinked,
    setPolicyMsgViolations: setPolicyMsgViolations,
    pkgFareClasses: [],
    pkgSortProcess: pkgSortProcess,
    pkgSortAsc: true,
  };

  $rootScope.$on('cartService:rail:removed', service.checkFareFilters);

  function init() {
    service.resultsMaster = [];
    service.results = [];
    service.flightMessages = [];
    service.fareClasses = [];
    service.hasLoaded = false;
    service.hasPackages = false;
    service.hasResults = false;
    service.showFareCode = settingsService.settings.FlightConfiguration.EnableFareCodeDisplay || false;
  }

  function getResults(searches, callback) {
    let searchRequest = _.clone(searches);
    service.hasBackendError = false;
    service.hasLoaded = false;
    service.hasPackages = false;
    const apiUrl = apiService.getUrl('travel');

    _.forEach(searchRequest, (search) => {
      search.PaxCount = search.Passengers.length;
      search.Searches = [
        {
          Identifier: search.Identifier,
          Type: 'Flight',
        },
      ];
    });

    $http[apiService.isMockActive('travel') ? 'get' : 'post'](apiUrl, searchRequest, { errorMessage: 'searching for travel' })
      .success((data) => {
        service.flightMessages = data.DisplayMessages;
        service.policyMessagePopups = data.PolicyMessagePopups;
        service.policyViolations = data.PolicyViolations;
        service.packageResults = null;
        service.hasPackages = false;
        const trips = data.TravelAvailabilityResponses.map((result, resultIndex) => prepData(result, resultIndex, data.length));

        if (data.Packages && data.Packages.length) {
          service.packageResults = pkgPrepData(data, trips);
          service.hasPackages = true;
        }
        _.forEach(trips, (result, resultIndex) => {
          if (_.isInteger(result.matchingResultsIndex)) {
            result.matchingResultsIndexes = getMatchingIndexes(resultIndex, result.matchingResultsIndex);
            result.resultsFilters.filters.cartItems.settings.matchingResultsIndexes = result.matchingResultsIndexes;
          }
        });

        service.results = trips;
        service.resultsMaster = $filter('deepCopyFilter')(service.results);
        service.hasResults = trips.some((trip) => trip.searchResults.length > 0);
        service.hasLoaded = true;

        if (typeof callback === 'function') callback();
      })
      .error(() => {
        service.hasBackendError = true;
        if (typeof callback === 'function') callback();
      });
  }

  function setResults(results) {
    service.results = results;
  }

  function getSeatMap(flights) {
    const deferred = $q.defer();
    const apiUrl = apiService.getUrl('travelSeatMap');

    $http
      .post(apiUrl, {
        SeatMapSearches: flights,
      })
      .success((response) => {
        deferred.resolve(response);
      })
      .error(() => {
        deferred.reject();
      });

    return deferred.promise;
  }

  function getFareClassGroupTypes(flight) {
    const groupTypes = {};

    _.forEach(flight.FareClasses, (fareClass) => {
      if (fareClass) {
        if (!groupTypes[fareClass.GroupType]) {
          groupTypes[fareClass.GroupType] = fareClass.ResBookDesignCode[0];
        }
      }
    });

    return _.isEmpty(groupTypes) ? undefined : groupTypes;
  }

  function getSeatmapSearch(gdsProvider, from, to, outboundFlight, inboundFlight, parentFlight) {
    return {
      OriginLocationCode: from,
      DestinationLocationCode: to,
      FlightNumber: outboundFlight.TripNumber,
      CarrierCode: outboundFlight.Carrier,
      OperatingCarrierCode: outboundFlight.OperatingCarrier,
      OperatingCarrierFlightNumber: outboundFlight.OperatingCarrierTripNumber,
      DepartureDateTimeOffset: outboundFlight.DepartureDateTimeOffset,
      ArrivalDateTimeOffset: inboundFlight.DepartureDateTimeOffset,
      GdsProvider: gdsProvider,
      FareClassGroupTypes: getFareClassGroupTypes(parentFlight),
    };
  }

  function getSeatmapSearches(flight) {
    const searches = [];

    if (flight.Legs.length === 0) {
      searches.push(
        getSeatmapSearch(flight.GdsProvider, flight.OriginLocationCode, flight.DestinationLocationCode, flight, flight, flight)
      );
    } else {
      searches.push(
        getSeatmapSearch(flight.GdsProvider, flight.OriginLocationCode, flight.Legs[0].Origin.Code, flight, flight.Legs[0], flight)
      );

      _.forEach(flight.Legs, (leg, legIndex) => {
        if (legIndex === flight.Legs.length - 1) {
          searches.push(getSeatmapSearch(flight.GdsProvider, leg.Origin.Code, flight.DestinationLocationCode, leg, flight, flight));
        } else {
          searches.push(
            getSeatmapSearch(
              flight.GdsProvider,
              leg.Origin.Code,
              flight.Legs[legIndex + 1].Origin.Code,
              leg,
              flight.Legs[legIndex + 1],
              flight
            )
          );
        }
      });
    }

    return searches;
  }

  function getTravelDetail(travel) {
    const deferred = $q.defer();

    if (travel.Details.length) {
      deferred.resolve(travel.Details);
    } else {
      const apiUrl = apiService.getUrl('travelDetail', {
        type: travel.Type,
        travelId: travel.Identifier,
      });

      const params = {
        searchIdentifier: travel.SearchIdentifier,
      };

      $http
        .get(apiUrl, { params: params })
        .success((data) => {
          travel.Details = data;
          deferred.resolve(travel.Details);
        })

        .error(() => {
          deferred.reject();
        });
    }

    return deferred.promise;
  }

  function getFareRules(sector, fare) {
    const deferred = $q.defer();

    if (fare.RemarksCollection) {
      const fareRuleData = () => {
        let text = '';
        _.forEach(fare.RemarksCollection, (obj) => {
          text = text + obj.Code + '\n' + obj.Value + '\n\n';
        });
        return text;
      };

      const fareRules = {
        Type: 'Text',
        Data: fareRuleData(),
      };

      deferred.resolve(fareRules);
    } else {
      const apiUrl = apiService.getUrl('travelFareRules', {
        type: sector.Type,
        travelId: sector.Identifier,
        fareId: fare.Identifier,
      });

      const params = {
        searchIdentifier: sector.SearchIdentifier,
      };

      $http
        .get(apiUrl, { params: params })
        .success((data) => {
          deferred.resolve(data);
        })

        .error(() => {
          deferred.reject();
        });
    }

    return deferred.promise;
  }

  function formatBundle(bundle, baseFare, index) {
    let bundleItems = bundle.Description.split(/\s*and\s*|,\s*/);
    let formattedBundle = {
      id: index,
      brandedFareName: baseFare.FareName,
      items: bundleItems,
      price: bundle.Price,
      currencyCode: bundle.CurrencyCode,
      selected: false,
    };
    return formattedBundle;
  }

  function prepData(data, index, totalSets) {
    let obj = {
      selected: false,
      index: index,
      matchingResultsIndex: data.MatchingResultsIndex,
      searchDate: data.Date,
      destination: data.Destination,
      origin: data.Origin,
      searchResults: data.Results,
      classes: [],
      economyClasses: [],
      activeSort: null,
      resultLimitIncrement: 20,
      resultLimit: getResultLimit(totalSets),
      resultsFilters: {
        filters: travelFiltersService.getFilters(),
        searchFilterTotal: 0,
      },
    };
    _.forEach(data.Results, (item) => {
      let fareClasses = item.FareClasses;

      item.$state = {};
      item.TotalFares = 0;
      item.SupplierName = item.SupplierName || item.CarrierName;
      item.ReturnPricing = _.isInteger(obj.matchingResultsIndex);

      // TODO move to a filter
      if (item.Rating) {
        item.Rating.OnTimeLevel = item.Rating.OntimePercent < 0.7 ? 'low' : item.Rating.OntimePercent < 0.9 ? 'mid' : 'high';
      }

      angular.forEach(fareClasses, (fare, key) => {
        if (fare === null) {
          return delete fareClasses[key];
        }

        fare.$state = {};
        fare.Class = key;

        const defaultFare = _.omit(fare, 'AdditionalFares');

        if (APP_CONFIG.bundles.enabled) {
          defaultFare.Bundles = _.map(fare.Bundles, (bundle, index) => formatBundle(bundle, fare, index));
        }

        fare.AdditionalFares.unshift(defaultFare);

        fare.AdditionalFares = _.map(fare.AdditionalFares, (additionalFare) => {
          additionalFare.$state = {};
          additionalFare.FareCode = additionalFare.FareCode ? additionalFare.FareCode.trim() : '';
          additionalFare.FareCodeDescription = additionalFare.FareCode ? `(${additionalFare.FareCode})` : '';
          additionalFare.MatchingResultsIndex = data.MatchingResultsIndex;
          additionalFare.Class = key;
          additionalFare.Bundles = APP_CONFIG.bundles.enabled
            ? _.map(additionalFare.Bundles, (bundle, index) =>
                bundle.items != null ? bundle : formatBundle(bundle, additionalFare, index)
              )
            : [];

          return additionalFare;
        });

        if (fare.GroupType === 'Economy') {
          if (!obj.economyClasses.includes(fare.Class)) {
            obj.economyClasses.push(fare.Class);
          }
        }

        item.TotalFares += fare.AdditionalFares.length;
      });

      obj.classes = obj.classes.concat(Object.keys(fareClasses));

      let priceFilterSettings = obj.resultsFilters.filters.priceRange.settings;

      if (item.MaxRate > priceFilterSettings.maxValue) {
        const roundedMaxRate = Math.ceil(item.MaxRate);
        priceFilterSettings.maxValue = roundedMaxRate;
        priceFilterSettings.defaultMax = roundedMaxRate;
      }

      item.TripDays = calculateDayChanges(item.DepartureDateTime, item.ArrivalDateTime);
    });

    obj.resultsFilters.filters.carrier.settings.options = getCarrierOptions(data.Results);

    let maxRate = getMaxRate(data.Results);
    obj.resultsFilters.filters.priceRange.settings.defaultMax = maxRate;
    obj.resultsFilters.filters.priceRange.settings.maxValue = maxRate;

    let maxCarbonEmission = getMaxCarbonEmission(data.Results);
    obj.resultsFilters.filters.carbonFootprintRange.settings.defaultMax = maxCarbonEmission;
    obj.resultsFilters.filters.carbonFootprintRange.settings.maxValue = maxCarbonEmission;
    obj.resultsFilters.filters.cartItems.settings.resultsIndex = index;
    obj.resultsFilters.filters.cartItems.settings.matchingResultsIndex = data.MatchingResultsIndex;

    // countFilteredResults(obj);

    buildFareClasses(obj.classes);

    obj.classes = service.fareClasses;
    OrderSelectedItems(data, index);
    return obj;
  }

  function calculateDayChanges(departureDateTime, arrivalDateTime) {
    const depart = new Date(departureDateTime);
    const arrive = new Date(arrivalDateTime);
    if (isNaN(depart.getTime()) || isNaN(arrive.getTime())) return 0;

    const departDate = new Date(depart.getFullYear(), depart.getMonth(), depart.getDate());
    const arriveDate = new Date(arrive.getFullYear(), arrive.getMonth(), arrive.getDate());

    const diff = arriveDate.getTime() - departDate.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  function OrderSelectedItems(data, index) {
    if (!($stateParams.selectedItems && $stateParams.selectedItems.length > 0 && data && data.Results)) return;
    for (let i = 0; i < $stateParams.selectedItems.length; i++) {
      for (let r = 0; r < data.Results.length; r++) {
        if ($stateParams.selectedItems[i] && data.Results[r].TripIdentifier === $stateParams.selectedItems[i].TripIdentifier) {
          data.Results.splice(0, 0, data.Results.splice(r, 1)[0]);
          $stateParams.selectedItems[i].SearchIdentifier = data.Results[r].SearchIdentifier;
          $stateParams.selectedItems[i].SearchIndex = index;
          return;
        }
      }
    }
  }

  function getResultLimit(totalSets) {
    const MAX_TOTAL = 100;

    return totalSets === 3 ? 30 : MAX_TOTAL / totalSets;
  }

  function getMaxRate(results) {
    return _.reduce(
      results,
      (maxRate, result) => {
        return Math.ceil(result.MaxRate > maxRate ? result.MaxRate : maxRate);
      },
      0
    );
  }

  function getMaxCarbonEmission(results) {
    return _.reduce(
      results,
      (minCarbonEmission, result) => {
        return Math.ceil(result.MinCarbonEmission > minCarbonEmission ? result.MinCarbonEmission : minCarbonEmission);
      },
      0
    );
  }

  function getCarrierOptions(results) {
    const uniqueResults = _.uniqBy(results, (item) => {
      return item.Carrier;
    });

    const orderedResults = _.orderBy(uniqueResults, (item) => {
      return item.CarrierName.toLowerCase();
    });

    return _.map(orderedResults, (item) => {
      return {
        value: item.Carrier,
        label: item.CarrierName,
        id: `Carrier-${item.Carrier}`,
      };
    });
  }

  function orderFareClasses(fareClasses) {
    const fareBucketClasses = settingsService.settings.FlightConfiguration.FareBucketClasses;

    if (!fareBucketClasses) {
      return fareClasses;
    }

    return _.orderBy(fareClasses, (fareClass) => {
      const fareBucketClassSettings = _.find(fareBucketClasses, (bucketClass) => {
        return bucketClass.Name === fareClass;
      });

      return fareBucketClassSettings ? fareBucketClassSettings.Sort : Number.MAX_SAFE_INTEGER;
    });
  }

  function buildFareClasses(classes) {
    service.fareClasses = service.fareClasses.concat(classes);
    service.fareClasses = _.uniq(service.fareClasses);
    service.fareClasses = orderFareClasses(service.fareClasses);

    angular.forEach(service.results, (result) => {
      result.classes = service.fareClasses;
    });
  }

  function addFilters(filters, index, clear) {
    if (clear) {
      service.results[index].resultsFilters.filters = {};
    }

    _.forOwn(filters, (filter, key) => {
      service.results[index].resultsFilters.filters[key] = filter;
    });

    process(index);
  }

  function updateFilters(filters, index, clear) {
    if (clear) {
      service.results[index].resultsFilters.filters = {};
    }

    _.forOwn(filters, (filter, key) => {
      service.results[index].resultsFilters.filters[key] = filter;
    });

    process(index);
  }

  function clearFilters(index) {
    service.results[index].selected = false;

    _.forOwn(service.results[index].resultsFilters.filters, (filter) => {
      if (_.isFunction(filter.settings.clear)) {
        filter.settings.clear();
      }
    });
  }

  function addFilter(filter, index, clear) {
    if (clear) {
      clearFilters(index);
    }

    if (filter) {
      service.results[index].resultsFilters.filters[filter.name] = filter;
    }

    process(index);
  }

  function removeFilter(filterName, index) {
    delete service.results[index].resultsFilters.filters[filterName];

    process(index);
  }

  function sort(sortOption, index) {
    service.results[index].searchResults.activeSort = sortOption;
    process(index);
  }

  function getMatchingIndexes(currentIndex, matchingIndex) {
    let resultsSet = service.results[matchingIndex];
    let indexes = [matchingIndex];

    if (service.results.length > 2) {
      service.results.forEach((item, i) => {
        if (
          (item.matchingResultsIndex == matchingIndex ||
            item.matchingResultsIndex == currentIndex ||
            item.orderBy == resultsSet.matchingResultsIndex) &&
          i !== currentIndex &&
          indexes.indexOf(i) === -1
        ) {
          indexes.push(i);
        }
      });
    }
    return indexes;
  }

  function processAllLinked(currentIndex, matchingIndex) {
    getMatchingIndexes(currentIndex, matchingIndex).forEach((ind) => {
      process(ind);
    });
  }

  function process(index) {
    let resultsSet = service.results[index];
    let processedResults = _.clone(service.resultsMaster[index].searchResults);
    let clearActiveFilters = false;

    resultsSet.resultsFilters.searchFilterTotal = 0;
    Object.values(resultsSet.resultsFilters.filters).forEach((filter) => {
      if (!filter.settings.isActive()) return;
      const clearActiveFilters = filter.clearActiveFilters && typeof filter.settings.clear === 'function';
      if (filter.isResultsFilter) resultsSet.resultsFilters.searchFilterTotal++;
      processedResults = clearActiveFilters ? filter.settings.clear() : $filter(filter.filterName)(processedResults, filter.settings);
    });

    if (resultsSet.activeSort) {
      // custom sorting needed for carbon emissions, so > 0 and non null list in ascending order first
      // == 0 and null display last
      if (resultsSet.activeSort.sortField === 'MinCarbonEmission') {
        resultsSet.searchResults = processedResults.sort(sortCarbonEmissions());
        return;
      }

      processedResults = _.orderBy(processedResults, (result) => {
        return result[resultsSet.activeSort.sortField];
      });

      if (resultsSet.activeSort.isReverse) {
        processedResults = processedResults.reverse();
      }
    }

    resultsSet.searchResults = processedResults;
  }

  function sortCarbonEmissions() {
    return function (a, b) {
      if (a.MinCarbonEmission === null) return 1; // return 1 so that b goes first
      if (b.MinCarbonEmission === null) return -1; // return -1 so that a goes first
      if (a.MinCarbonEmission === 0) return 1; // return 1 so that b goes first
      if (b.MinCarbonEmission === 0) return -1; // return -1 so that a goes first

      // return -1 so that a goes first, as it is < b, otherwise return 1 so b goes first
      return a.MinCarbonEmission < b.MinCarbonEmission ? -1 : 1;
    };
  }

  function itemAddedToCart(parentIndex, row, item) {
    const resultsSet = service.results[parentIndex];
    resultsSet.selected = true;
    setAnchoredFlight(row, item);

    let selectedFare = {
      type: 'flight',
      fare: item,
      selectedBundle: item.Bundles.filter((bundle) => bundle.selected === true)[0],
      result: row,
      orderBy: parentIndex,
      matchingIndex: resultsSet.matchingResultsIndex,
      matchingIndexes: resultsSet.matchingResultsIndexes,
    };

    if (cartService.items.filter((item) => item.orderBy === parentIndex).length > 0) {
      for (let i = 0; i < cartService.items.length; i++) {
        if (cartService.items[i].orderBy === parentIndex) {
          cartService.items[i] = selectedFare;
          break;
        }
      }
    } else {
      // lbCartPanel.js will detect this push via $scope.$watchCollection(vm.cartService.items).
      cartService.items.push(selectedFare);
    }
    let matchingCartItems = cartService.items.filter(
      (item) =>
        item.orderBy !== parentIndex &&
        (item.orderBy === resultsSet.matchingResultsIndex ||
          (resultsSet.matchingResultsIndexes && resultsSet.matchingResultsIndexes.indexOf(item.orderBy) !== -1))
    );
    if (matchingCartItems) {
      matchingCartItems.forEach((matchingCartItem) => {
        let matchingFare = _.find(matchingCartItem.fare.MatchedFares, (fare) => {
          return fare.Identifier === selectedFare.fare.Identifier;
        });
        if (!matchingFare) {
          let additionalFares = matchingCartItem.result.FareClasses[matchingCartItem.fare.Class].AdditionalFares;
          for (let i = 0; i < additionalFares.length; i++) {
            let subFare = additionalFares[i].MatchedFares.find((matchedFare) => selectedFare.fare.Identifier === matchedFare.Identifier);
            if (subFare) {
              let policyException = matchingCartItem.fare.PolicyException;
              matchingCartItem.fare = additionalFares[i];
              matchingCartItem.fare.PolicyException = policyException;
            }
          }
        }
      });
    }

    process(parentIndex);
    if (_.isInteger(resultsSet.matchingResultsIndex)) {
      processAllLinked(parentIndex, resultsSet.matchingResultsIndex);
    }
  }

  function setAnchoredFlight(flightResult, fare) {
    let anchoredFlight = flightResult.Identifier;
    let searchIdentifier = flightResult.SearchIdentifier;
    itinerariesService.itinerary.Search.Flights.forEach((flight) => {
      let searchItem = flight.Searches.find((s) => s.Identifier == searchIdentifier);
      if (searchItem) {
        flight.anchoredFlight = anchoredFlight;
        flight.anchoredFare = fare;
        flight.anchoredTripIdentifier = { TripIdentifier: flightResult.TripIdentifier, Fare: fare, Flight: flightResult };
      }
    });
  }

  function getFareIsOutOfPolicyMsg(fare) {
    let msg = '';
    let separator = '<br/>';
    if (!(fare && fare.PolicyExceptionDetails)) return msg;

    if (fare.PolicyExceptionDetails.InPolicy === false) {
      if (fare.PolicyExceptionDetails.PolicyMessage) {
        msg += (msg != '' ? separator : '') + fare.PolicyExceptionDetails.PolicyMessage;
      } else {
        msg += (msg != '' ? separator : '') + 'Out of Policy: Lost Savings';
      }
    }
    if (fare.PolicyExceptionDetails.PolicyViolations && fare.PolicyExceptionDetails.PolicyViolations.length > 0) {
      service.setPolicyMsgViolations(fare.PolicyExceptionDetails);
      msg +=
        (msg != '' ? separator : '') +
        fare.PolicyExceptionDetails.PolicyViolations.map((policy) =>
          policy.ViolationMessage ? policy.ViolationMessage : 'Out of Policy'
        ).join(separator);
    }
    return msg;
  }

  function setPolicyMsgViolations(policyExceptionDetails) {
    if (
      service.policyViolations &&
      service.policyViolations.length &&
      policyExceptionDetails &&
      policyExceptionDetails.PolicyViolations &&
      policyExceptionDetails.PolicyViolations.length
    ) {
      let items = [];
      policyExceptionDetails.PolicyViolations.forEach((item) => {
        let lookupUp = service.policyViolations.find((f) => f.id === item.id);
        if (lookupUp) {
          items.push(lookupUp);
        } else {
          items.push(item);
        }
      });
      policyExceptionDetails.PolicyViolations = items;
    }

    if (
      service.policyMessagePopups &&
      service.policyMessagePopups.length &&
      policyExceptionDetails &&
      policyExceptionDetails.PolicyMessagePopups &&
      policyExceptionDetails.PolicyMessagePopups.length
    ) {
      let items = [];
      policyExceptionDetails.PolicyMessagePopups.forEach((item) => {
        let lookupUp = service.policyMessagePopups.find((f) => f.id === item.id);
        if (lookupUp) {
          items.push(lookupUp);
        } else {
          items.push(item);
        }
      });
      policyExceptionDetails.PolicyMessagePopups = items;
    }
  }

  function findFare(fareIdentifier, trips) {
    for (const trip of trips) {
      for (const result of trip.searchResults) {
        const foundFare = Object.values(result.FareClasses).find((fare) => {
          if (fare.Identifier === fareIdentifier) return fare;
          return fare.AdditionalFares.find((af) => {
            if (af.Identifier === fareIdentifier) return af;
          });
        });
        if (foundFare) return foundFare;
      }
    }
    return {};
  }

  function pkgPrepData(data, trips) {
    service.hasPackages = true;
    service.pkgFareClasses = [...new Set(data.Packages.flatMap((pkg) => Object.keys(pkg.FareClasses)))];

    const order = settingsService.settings.FlightConfiguration.FareBucketClasses;
    order.sort((a, b) => a.Sort - b.Sort);
    service.pkgFareClasses = order.filter((item) => service.pkgFareClasses.includes(item.Name)).map((item) => item.Name);

    const packages = data.Packages.map((pkg, i) => {
      for (const fc in pkg.FareClasses) {
        const fareClass = pkg.FareClasses[fc];
        fareClass.Details.PackageFares = fareClass.FareIdentifiers.map((f) => findFare(f, trips));
        fareClass.Fares = [fareClass.Details];
        fareClass.AdditionalFares.forEach((af) => {
          af.Details.PackageFares = af.FareIdentifiers.map((f) => findFare(f, trips));
          fareClass.Fares.push(af.Details);
        });
        fareClass.Fares.forEach((fare) => (fare.visible = true));
        fareClass.Fares.sort((a, b) => a.Amount - b.Amount);
        fareClass.DisplayFare = fareClass.Fares[0];
        fareClass.visible = true;
      }

      pkg.FareClasses.$state = {};

      const packageFares = service.pkgFareClasses.reduce((fares, fareClass) => {
        fares[fareClass] = pkg.FareClasses[fareClass] || null;
        return fares;
      }, {});

      const cleanPackage = {
        FareClasses: packageFares,
        Segments: [],
        visible: true,
        cheapest: false,
        defaultSort: i,
        id: Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
      };

      pkg.Segments.forEach((segment) => {
        const resultTrip = data.TravelAvailabilityResponses.find((trip) => trip.Identifier === segment.TripIdentifier);
        if (!resultTrip) return;

        const cleanSegment = {
          origin: resultTrip.Origin,
          destination: resultTrip.Destination,
          identifier: resultTrip.Identifier,
          visible: true,
        };

        const resultFlight = pkgFindFlight(segment.FlightIdentifier, resultTrip.Results);
        if (!resultFlight) return;

        resultFlight.TripDays = calculateDayChanges(resultFlight.DepartureDateTime, resultFlight.ArrivalDateTime);

        cleanSegment.flight = pkgPrepFlight(resultFlight);
        cleanPackage.Segments.push(cleanSegment);
      });
      return cleanPackage;
    });

    pkgMarkCheapest(packages);
    pkgMarkPreferred(packages);

    service.packageResultsMaster = packages;

    return packages;
  }

  function pkgMarkPreferred(packages) {
    packages.forEach((pkg) => (pkg.preferred = pkg.Segments.some((segment) => segment.flight.Preferred)));
  }

  function pkgMarkCheapest(packages) {
    const distinctCarriers = new Set();
    packages.forEach((pkg) => pkg.Segments.forEach((segment) => distinctCarriers.add(segment.flight.Carrier)));
    const carriers = [...distinctCarriers];
    const cheapestFares = {};
    packages.forEach((pkg) => {
      pkg.Segments.forEach((segment) => {
        const { Carrier } = segment.flight;
        if (carriers.includes(Carrier)) {
          Object.entries(pkg.FareClasses)
            .filter(([fareClassName, fareClass]) => fareClass)
            .forEach(([fareClassName, fareClass]) => {
              const { Amount } = fareClass.Details;
              if (!cheapestFares[Carrier] || Amount < cheapestFares[Carrier].Amount) {
                cheapestFares[Carrier] = { fareClassName, Amount };
                pkg.cheapest = true;
                fareClass.Details.cheapest = true;
              }
            });
        }
      });
    });
  }

  function pkgPrepFlight(flight) {
    const equiptmentTypeArray = flight.AircraftName.split('/');
    if (flight.Details && flight.Details.length === equiptmentTypeArray.length) {
      for (let i = 0; i < flight.Details.length; i++) {
        if (!flight.Details[i].EquipmentType) flight.Details[i].EquipmentType = equiptmentTypeArray[i];
      }
    }

    return flight;
  }

  function pkgFindFlight(flightIdentifier, flights) {
    return flights.find((flight) => flight.Identifier === flightIdentifier);
  }

  function pkgSortProcess(data) {
    if (!data.property) return;

    const getPropertySum = (pkg, property) => {
      if (property === 'Amount') {
        const cleanedFareClasses = Object.values(pkg.FareClasses).filter((fc) => fc !== null);
        return Math.min(...cleanedFareClasses.map((fc) => fc.Details.Amount));
      } else if (property === 'defaultSort') {
        return pkg[property];
      } else {
        return pkg.Segments.reduce((sum, sgmt) => sum + sgmt.flight[property], 0);
      }
    };

    service.packageResults.sort((a, b) => {
      const aSum = getPropertySum(a, data.property);
      const bSum = getPropertySum(b, data.property);
      return service.pkgSortAsc ? aSum - bSum : bSum - aSum;
    });

    service.pkgSortAsc = !service.pkgSortAsc;
  }

  return service;
};

travelService.$inject = $inject;

export default travelService;



// WEBPACK FOOTER //
// ./src/services/travelService.js